<section aria-labelledby="key-management-title">
  <div class="section-heading">
    <div>
      <h2 id="key-management-title">{{ toolTitle() }}</h2>
    </div>
  </div>

  @if (showNavigation()) {
    <div class="sub-navigation" role="tablist" aria-label="Key tools">
      <button type="button" role="tab" [attr.aria-selected]="activeTool() === 'saved'" [class.active]="activeTool() === 'saved'" (click)="openTool('saved')">Retrieve access keys</button>
      <button type="button" role="tab" [attr.aria-selected]="activeTool() === 'request-all'" [class.active]="activeTool() === 'request-all'" (click)="openTool('request-all')">Request all compartment keys</button>
      <button type="button" role="tab" [attr.aria-selected]="activeTool() === 'retrieve-all'" [class.active]="activeTool() === 'retrieve-all'" (click)="openTool('retrieve-all')">Retrieve all compartment keys</button>
    </div>
  }

  <div class="workbench">
    @switch (activeTool()) {
      @case ('saved') {
        <form class="form-surface" #savedForm="ngForm" (ngSubmit)="retrieveSavedKeys()">
          <div class="form-field">
            <label for="saved-key-request-id">Request ID</label>
            <input id="saved-key-request-id" name="savedRequestId" class="form-control" [(ngModel)]="savedRequestId" required>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="savedForm.invalid">Retrieve keys</button>
          </div>
        </form>
      }
      @case ('request-all') {
        <form class="form-surface" #allKeysForm="ngForm" (ngSubmit)="requestAllKeys()">
          <div class="form-grid">
            <div class="form-field">
              <label for="all-keys-lock-id">Lock ID</label>
              <input id="all-keys-lock-id" name="lockId" class="form-control" [(ngModel)]="lockId">
            </div>
            <div class="form-field">
              <label for="all-keys-hardware-id">Hardware ID</label>
              <input id="all-keys-hardware-id" name="hardwareId" class="form-control" [(ngModel)]="hardwareId">
            </div>
            <div class="form-field">
              <label for="all-keys-provider">System provider</label>
              <input id="all-keys-provider" name="provider" class="form-control" [(ngModel)]="provider" required>
            </div>
            <div class="form-field">
              <label for="all-keys-country">Country</label>
              <select id="all-keys-country" name="country" class="form-select" [(ngModel)]="country" required>
                <option value="SE">SE</option>
                <option value="DK">DK</option>
                <option value="NO">NO</option>
                <option value="FI">FI</option>
              </select>
            </div>
            <div class="form-field">
              <label for="all-keys-requester">Requester ID</label>
              <input id="all-keys-requester" name="requesterId" class="form-control" [(ngModel)]="requesterId" readonly required>
            </div>
            <div class="form-field">
              <label for="all-keys-shipper">Shipper name</label>
              <input id="all-keys-shipper" name="shipperName" class="form-control" [(ngModel)]="shipperName" readonly required>
            </div>
            <div class="form-field">
              <label for="all-keys-date">TTL date</label>
              <input id="all-keys-date" name="ttlDate" type="date" class="form-control" [(ngModel)]="ttlDate" required>
            </div>
            <div class="form-field">
              <label for="all-keys-time">TTL time</label>
              <input id="all-keys-time" name="ttlTime" type="time" class="form-control" [(ngModel)]="ttlTime">
            </div>
            <div class="form-field">
              <label for="all-keys-request-id">Request ID</label>
              <div class="input-group">
                <input id="all-keys-request-id" name="allKeysRequestId" class="form-control" [(ngModel)]="allKeysRequestId" readonly required>
                <button type="button" class="btn btn-outline-secondary" (click)="regenerateAllKeysRequestId()">Refresh</button>
              </div>
            </div>
            <div class="form-field">
              <label for="all-keys-user">Key user ID</label>
              <input id="all-keys-user" name="keyUserId" class="form-control" [(ngModel)]="keyUserId" required>
            </div>
          </div>

          <details class="form-section">
            <summary>Encryption and SDK identities</summary>
            <div class="form-grid form-section-body">
              <div class="form-field full">
                <label for="all-keys-public-key">Device public key</label>
                <textarea id="all-keys-public-key" class="form-control code-input" rows="4" [value]="publicKey" readonly></textarea>
              </div>
              <div class="form-field">
                <label>Key algorithm</label>
                <input class="form-control" [value]="algorithm" readonly>
              </div>
              <div class="form-field">
                <label>Encryption algorithm</label>
                <input class="form-control" [value]="encryption" readonly>
              </div>
            </div>
            <div class="identity-heading">
              <span>Lock provider SDK identities</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="addSdkIdentity()">Add identity</button>
            </div>
            @for (identity of sdkIdentities(); track identity.id) {
              <div class="identity-row">
                <input name="allSdkKey-{{ identity.id }}" class="form-control" aria-label="SDK identity key" [(ngModel)]="identity.key">
                <span>=</span>
                <input name="allSdkValue-{{ identity.id }}" class="form-control" aria-label="SDK identity value" [(ngModel)]="identity.value">
                <button type="button" class="btn btn-outline-danger" (click)="removeSdkIdentity(identity.id)">Remove</button>
              </div>
            }
          </details>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="allKeysForm.invalid">Request all keys</button>
          </div>
        </form>
      }
      @case ('retrieve-all') {
        <form class="form-surface" #retrieveAllForm="ngForm" (ngSubmit)="retrieveAllKeys()">
          <div class="form-field">
            <label for="retrieve-all-request-id">Request ID</label>
            <input id="retrieve-all-request-id" name="allKeysRequestId" class="form-control" [(ngModel)]="allKeysRequestId" required>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="retrieveAllForm.invalid">Retrieve all keys</button>
          </div>
        </form>
      }
    }

    <div>
      <app-api-endpoint [endpoint]="endpoint()" />

      <aside class="guidance-panel" aria-label="Key management instructions">
        @switch (activeTool()) {
          @case ('saved') {
            <p><strong>Description:</strong> Lists downloaded access keys from a previous key request.</p>
            <p><strong>Expected Result:</strong> A list of downloaded access keys.</p>
          }
          @case ('request-all') {
            <p><strong>Description:</strong> Maintenance operation for opening all compartments or lockedspaces.</p>
            <p><strong>Expected Result:</strong> Access keys for every compartment, or HTTP 204 when the lock provider initiates opening all compartments.</p>
          }
          @case ('retrieve-all') {
            <p><strong>Description:</strong> Lists all downloaded compartment access keys.</p>
            <p><strong>Expected Result:</strong> A list of all downloaded compartment access keys.</p>
          }
        }
      </aside>

      <app-api-result
        [endpoint]="endpoint()"
        [showEndpoint]="false"
        [request]="call.request()"
        [response]="call.response()"
        [error]="call.error()"
      />
    </div>
  </div>
</section>